import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import {
  NativePurchases,
  PURCHASE_TYPE,
  type Product,
} from "@capgo/native-purchases";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";

type Platform = "ios" | "android" | "web";
type AuthProvider = "apple" | "google";
type PasswordlessChannel = "phone" | "email";

interface PublicMobileConfig {
  appId: string;
  appleIosClientId: string;
  appleServicesId: string;
  googleWebClientId: string;
  googleIosClientId: string;
}

interface Session {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

interface CatalogProduct {
  id: string;
  name: string;
  description: string;
  kind: "digital_membership" | "service_fee";
  provider: "apple" | "google" | "antom";
  platformProductId?: string;
  billingPeriod?: "month" | "year" | "one_time";
  displayPrice?: string;
  entitlementTier?: "basic" | "plus" | "pro";
}

interface RemoteConfig {
  authProviders: Record<AuthProvider | PasswordlessChannel, boolean>;
  products: CatalogProduct[];
}

interface MobileServiceApi {
  signIn(provider: AuthProvider): Promise<void>;
  requestCode(channel: PasswordlessChannel, destination: string): Promise<{ developmentCode?: string }>;
  verifyCode(channel: PasswordlessChannel, destination: string, code: string): Promise<void>;
  createAppointment(input: AppointmentInput): Promise<{ id: string }>;
  deleteAccount(): Promise<void>;
  signOut(): Promise<void>;
  openMembership(): Promise<void>;
  restorePurchases(): Promise<void>;
  refreshEntitlements(): Promise<void>;
}

interface AppointmentInput {
  serviceId: string;
  serviceTitle: string;
  customerName: string;
  contact: string;
  question: string;
  timeSlot: string;
  timezone: string;
}

declare global {
  interface Window {
    __ZHIJI_MOBILE__?: MobileServiceApi;
  }
}

const SESSION_KEY = "session";
const WEB_SESSION_KEY = "zhiji:session";
const WEB_TOKEN_KEY = "zhiji:token";
const AUTH_EVENT = "zhiji:auth";
const MEMBER_KEY = "zhiji:member";
const API_PREFIX = "/api/mobile/v1";

let apiBaseUrl = "";
let publicConfig: PublicMobileConfig;
let platform: Platform;
let remoteConfigPromise: Promise<RemoteConfig> | undefined;
let initializedProviders = false;
let currentSheet: HTMLElement | undefined;
let storeProducts = new Map<string, Product>();

export async function installMobileServices(options: {
  apiBaseUrl: string;
  version: string;
}): Promise<void> {
  apiBaseUrl = options.apiBaseUrl;
  platform = Capacitor.getPlatform() as Platform;
  publicConfig = readPublicConfig();
  installStyles();
  installNativeUi();
  if (Capacitor.isNativePlatform()) await SecureStorage.setKeyPrefix("zhiji_");
  await restoreSession();

  window.__ZHIJI_MOBILE__ = {
    signIn,
    requestCode,
    verifyCode,
    createAppointment,
    deleteAccount,
    signOut,
    openMembership,
    restorePurchases,
    refreshEntitlements,
  };

  if (Capacitor.isNativePlatform()) {
    installAuthTokenMigration();
    installDeepLinkHandler();
  }
  void refreshEntitlements();
}

function readPublicConfig(): PublicMobileConfig {
  const encoded = document
    .querySelector<HTMLMetaElement>('meta[name="zhiji-mobile-config"]')
    ?.content.trim();
  if (!encoded) throw new Error("缺少移动服务公开配置");
  return JSON.parse(atob(encoded)) as PublicMobileConfig;
}

async function restoreSession(): Promise<void> {
  const stored = await readStoredSession();
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return;
  const session = stored as unknown as Session;

  if (session.refreshToken) {
    try {
      const refreshed = await apiJson<Session>("/auth/refresh", {
        method: "POST",
        body: JSON.stringify({ refreshToken: session.refreshToken }),
        authenticated: false,
      });
      await saveSession(refreshed);
      return;
    } catch {
      // 网络不可用时仍可短暂使用尚未到期的 access token。
    }
  }

  if (!session.expiresAt || session.expiresAt > Date.now()) {
    if (typeof session.accessToken === "string" && session.accessToken) {
      localStorage.setItem(WEB_TOKEN_KEY, session.accessToken);
      window.dispatchEvent(new Event(AUTH_EVENT));
    }
  } else {
    localStorage.removeItem(WEB_TOKEN_KEY);
    clearCachedMembership();
    window.dispatchEvent(new Event(AUTH_EVENT));
  }
}

function installAuthTokenMigration(): void {
  window.addEventListener(AUTH_EVENT, () => {
    void (async () => {
      const token = localStorage.getItem(WEB_TOKEN_KEY);
      const stored = await readStoredSession();
      const session =
        stored && typeof stored === "object" && !Array.isArray(stored)
          ? (stored as unknown as Session)
          : undefined;

      if (!token) {
        await removeStoredSession();
        clearCachedMembership();
      } else if (session?.accessToken !== token) {
        await writeStoredSession({
          accessToken: token,
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        });
      }
    })().catch(() => notify("登录状态暂时无法安全保存，请稍后重试"));
  });
}

async function saveSession(session: Session): Promise<void> {
  if (typeof session?.accessToken !== "string" || !session.accessToken) {
    throw new Error("登录服务返回异常，请稍后重试");
  }
  await writeStoredSession(session);
  localStorage.setItem(WEB_TOKEN_KEY, session.accessToken);
  window.dispatchEvent(new Event(AUTH_EVENT));
}

async function signOut(): Promise<void> {
  const stored = await readStoredSession();
  const session =
    stored && typeof stored === "object" && !Array.isArray(stored)
      ? (stored as unknown as Session)
      : undefined;
  if (session?.refreshToken) {
    await apiJson("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    }).catch(() => undefined);
  }
  await removeStoredSession();
  localStorage.removeItem(WEB_TOKEN_KEY);
  clearCachedMembership();
  window.dispatchEvent(new Event(AUTH_EVENT));
}

async function ensureProvidersInitialized(): Promise<void> {
  if (initializedProviders) return;

  const socialOptions: Parameters<typeof SocialLogin.initialize>[0] = {
    apple: {
      clientId: platform === "ios" ? publicConfig.appleIosClientId : publicConfig.appleServicesId,
      redirectUrl: platform === "ios" ? "" : platform === "web" ? `${window.location.origin}/login` : `${apiBaseUrl}/api/mobile/v1/auth/apple/callback`,
      useProperTokenExchange: false,
    },
  };
  if (publicConfig.googleWebClientId) {
    socialOptions.google = {
      webClientId: publicConfig.googleWebClientId,
      iOSClientId: publicConfig.googleIosClientId || undefined,
      mode: "online",
    };
  }
  await SocialLogin.initialize(socialOptions);
  initializedProviders = true;
}

async function signIn(provider: AuthProvider): Promise<void> {
  const config = await getRemoteConfig().catch(() => undefined);
  if (!config?.authProviders[provider]) {
    notify(`${providerLabel(provider)}尚未完成平台配置`);
    return;
  }

  try {
    await ensureProvidersInitialized();
    let credential: Record<string, unknown>;

    if (provider === "apple") {
      const response = await SocialLogin.login({
        provider: "apple",
        options: { scopes: ["email", "name"], state: randomState() },
      });
      credential = {
        provider,
        idToken: response.result.idToken,
        authorizationCode: response.result.authorizationCode,
        profile: response.result.profile,
      };
    } else if (provider === "google") {
      const response = await SocialLogin.login({
        provider: "google",
        options: { scopes: ["profile", "email"], forcePrompt: false },
      });
      if (response.result.responseType !== "online") {
        throw new Error("Google 登录未返回身份令牌");
      }
      credential = { provider, idToken: response.result.idToken };
    } else {
      throw new Error("不支持的登录方式");
    }

    const session = await apiJson<Session>("/auth/exchange", {
      method: "POST",
      body: JSON.stringify(credential),
    });
    await saveSession(session);
    notify(`${providerLabel(provider)}成功`);
    window.location.assign(postLoginPath());
  } catch (error) {
    if (isUserCancellation(error)) return;
    notify(messageFrom(error, "登录未完成，请稍后重试"));
  }
}

async function requestCode(
  channel: PasswordlessChannel,
  destination: string,
): Promise<{ developmentCode?: string }> {
  return apiJson("/auth/code/request", {
    method: "POST",
    body: JSON.stringify({ channel, destination }),
    authenticated: false,
  });
}

async function verifyCode(
  channel: PasswordlessChannel,
  destination: string,
  code: string,
): Promise<void> {
  const session = await apiJson<Session>("/auth/code/verify", {
    method: "POST",
    body: JSON.stringify({ channel, destination, code }),
    authenticated: false,
  });
  await saveSession(session);
  window.location.assign(postLoginPath());
}

async function createAppointment(input: AppointmentInput): Promise<{ id: string }> {
  if (!localStorage.getItem(WEB_TOKEN_KEY)) {
    throw new Error(isEnglishLocale() ? "Sign in before booking." : "请先登录后再提交预约");
  }
  const result = await apiJson<{ appointment: { id: string } }>("/appointments", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return result.appointment;
}

async function deleteAccount(): Promise<void> {
  await apiJson("/account", { method: "DELETE", body: JSON.stringify({}) });
  await signOut();
  window.location.assign("/");
}

async function readStoredSession(): Promise<unknown> {
  if (Capacitor.isNativePlatform()) return SecureStorage.get(SESSION_KEY, false);
  const raw = localStorage.getItem(WEB_SESSION_KEY);
  if (!raw) return undefined;
  try { return JSON.parse(raw); } catch { return undefined; }
}

async function writeStoredSession(session: Session): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await SecureStorage.set(SESSION_KEY, session as unknown as Record<string, unknown>);
  } else {
    localStorage.setItem(WEB_SESSION_KEY, JSON.stringify(session));
  }
}

async function removeStoredSession(): Promise<void> {
  if (Capacitor.isNativePlatform()) await SecureStorage.remove(SESSION_KEY);
  else localStorage.removeItem(WEB_SESSION_KEY);
}

async function getRemoteConfig(): Promise<RemoteConfig> {
  remoteConfigPromise ??= apiJson<RemoteConfig>(
    `/config?platform=${platform}&channel=${platform === "ios" ? "app_store" : platform === "android" ? "google_play" : "web"}`,
    { authenticated: false },
  ).catch((error) => {
    remoteConfigPromise = undefined;
    throw error;
  });
  return remoteConfigPromise;
}

function installNativeUi(): void {
  let scheduled = false;
  const render = () => {
    scheduled = false;
    renderLoginPage();
    renderAccountDeletionAction();
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(render);
  };

  // Observe the packaged React tree only. The native overlay lives outside it;
  // observing our own writes schedules a render loop on every animation frame.
  new MutationObserver(schedule).observe(document.getElementById("root") ?? document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["disabled"],
  });
  window.addEventListener("popstate", schedule);
  schedule();

  document.addEventListener(
    "click",
    (event) => {
      const button = (event.target as Element | null)?.closest("button");
      if (!button) return;
      const label = button.textContent?.trim();
      if (matchesLocalized(label, "升级") && button.closest("#member")) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void openMembership();
      }
    },
    true,
  );
}

function renderAccountDeletionAction(): void {
  const existing = document.getElementById("zhiji-delete-account");
  if (window.location.pathname !== "/profile" || !localStorage.getItem(WEB_TOKEN_KEY)) {
    existing?.remove();
    return;
  }
  if (existing) return;
  const clearButton = [...document.querySelectorAll<HTMLButtonElement>("button")].find(
    (button) => matchesLocalized(button.textContent?.trim(), "清空全部数据"),
  );
  if (!clearButton?.parentElement) return;
  const button = document.createElement("button");
  button.id = "zhiji-delete-account";
  button.type = "button";
  button.textContent = isEnglishLocale() ? "Delete account" : "删除账号";
  button.className = clearButton.className;
  button.style.color = "#d88179";
  button.addEventListener("click", () => {
    const message = isEnglishLocale()
      ? "Permanently delete your account and cloud data? This cannot be undone."
      : "确定永久删除账号及云端数据吗？此操作无法撤销。";
    if (window.confirm(message)) void deleteAccount().catch((error) => notify(messageFrom(error, "删除账号失败，请稍后重试")));
  });
  clearButton.insertAdjacentElement("afterend", button);
}

function renderLoginPage(): void {
  const isLoginPage = window.location.pathname === "/login";
  const existing = document.getElementById("zhiji-native-login");
  if (!isLoginPage) {
    existing?.remove();
    document.body.classList.remove("zhiji-native-login-active");
    document.getElementById("root")?.removeAttribute("inert");
    document.getElementById("root")?.removeAttribute("aria-hidden");
    return;
  }
  if (existing) return;

  const en = isEnglishLocale();
  const text = (zh: string, english: string) => en ? english : zh;
  const screen = document.createElement("section");
  screen.id = "zhiji-native-login";
  screen.className = "zhiji-native-login-screen";
  screen.setAttribute("aria-label", text("登录或注册知几", "Sign in or create a Zhiji account"));
  screen.innerHTML = `
    <div class="zhiji-native-login-shell">
      <header class="zhiji-native-login-toolbar">
        <button type="button" data-login-close aria-label="${text("关闭登录", "Close sign in")}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <span>${text("知几", "ZHIJI")}</span>
      </header>
      <div class="zhiji-auth-layout">
        <aside class="zhiji-auth-intro">
          <div class="zhiji-native-login-mark" aria-hidden="true"><span>${en ? "Z" : "知"}</span></div>
          <p>${text("传统智慧，现代决策", "Timeless wisdom for modern decisions")}</p>
          <h1>${text("把每一次选择，变成更清醒的决定。", "Make every choice with greater clarity.")}</h1>
          <ul>
            <li>${text("跨设备同步个人档案与测算记录", "Sync profiles and readings across devices")}</li>
            <li>${text("保存六爻、梅花与每日指引", "Save divination and daily guidance")}</li>
            <li>${text("随时管理会员权益与预约", "Manage membership and bookings")}</li>
          </ul>
        </aside>
        <main class="zhiji-auth-card">
          <h2>${text("继续使用知几", "Continue to Zhiji")}</h2>
          <p class="zhiji-native-login-lead">${text("选择一种方式登录；首次验证会自动创建账号。", "Choose a sign-in method. Your account is created after first verification.")}</p>
          <div class="zhiji-native-provider-list" data-provider-list></div>
          <form class="zhiji-native-passwordless-form" data-passwordless-form novalidate hidden>
            <button type="button" class="zhiji-auth-change" data-change-method>${text("返回其他方式", "Choose another method")}</button>
            <label data-destination-label></label>
            <div class="zhiji-native-phone-field">
              <span data-prefix></span>
              <input data-destination autocomplete="username" />
            </div>
            <p class="zhiji-native-login-error" data-login-error role="alert"></p>
            <button class="zhiji-native-login-primary" type="button" data-send-code>${text("获取验证码", "Send verification code")}</button>
            <div class="zhiji-native-code-step" data-code-step hidden>
              <label>${text("验证码", "Verification code")}</label>
              <input data-code type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="${text("输入 6 位验证码", "Enter the 6-digit code")}" />
              <p class="zhiji-native-login-hint" data-code-hint>${text("验证码有效期为 10 分钟", "The code expires in 10 minutes")}</p>
              <button class="zhiji-native-login-primary" type="submit">${text("继续", "Continue")}</button>
            </div>
          </form>
          <p class="zhiji-auth-legal">${text("继续即表示你同意用户协议与隐私政策。", "By continuing, you agree to the Terms and Privacy Policy.")}</p>
        </main>
      </div>
    </div>`;

  const list = screen.querySelector<HTMLElement>("[data-provider-list]");
  const methods: Array<{ key: AuthProvider | PasswordlessChannel; label: string }> = [
    { key: "apple", label: text("使用 Apple 账号继续", "Continue with Apple") },
    { key: "google", label: text("使用 Google 账号继续", "Continue with Google") },
    { key: "phone", label: text("使用手机号继续", "Continue with phone") },
    { key: "email", label: text("使用邮箱继续", "Continue with email") },
  ];
  for (const method of methods) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `zhiji-native-provider zhiji-native-provider-${method.key}`;
    button.dataset.method = method.key;
    button.innerHTML = `${method.key === "phone" ? '<span class="zhiji-native-phone-icon">+86</span>' : method.key === "email" ? '<span class="zhiji-native-email-icon">@</span>' : providerIcon(method.key)}<span>${method.label}</span>`;
    button.addEventListener("click", () => {
      if (method.key === "apple" || method.key === "google") {
        void signIn(method.key);
      } else {
        showPasswordlessForm(screen, method.key);
      }
    });
    list?.append(button);
  }

  screen.querySelector("[data-login-close]")?.addEventListener("click", () => {
    if (window.history.length > 1) window.history.back();
    else window.location.assign("/");
  });
  screen.querySelector("[data-change-method]")?.addEventListener("click", () => {
    screen.querySelector<HTMLElement>("[data-passwordless-form]")?.setAttribute("hidden", "");
    list?.removeAttribute("hidden");
  });
  screen.querySelector("[data-send-code]")?.addEventListener("click", () => void submitCodeRequest(screen));
  screen.querySelector<HTMLFormElement>("[data-passwordless-form]")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitCodeVerification(screen);
  });

  document.body.append(screen);
  document.body.classList.add("zhiji-native-login-active");
  document.getElementById("root")?.setAttribute("inert", "");
  document.getElementById("root")?.setAttribute("aria-hidden", "true");
}

function showPasswordlessForm(screen: HTMLElement, channel: PasswordlessChannel): void {
  const en = isEnglishLocale();
  screen.dataset.channel = channel;
  screen.querySelector<HTMLElement>("[data-provider-list]")?.setAttribute("hidden", "");
  const form = screen.querySelector<HTMLElement>("[data-passwordless-form]");
  form?.removeAttribute("hidden");
  const label = screen.querySelector<HTMLElement>("[data-destination-label]");
  const prefix = screen.querySelector<HTMLElement>("[data-prefix]");
  const input = screen.querySelector<HTMLInputElement>("[data-destination]");
  if (label) label.textContent = channel === "phone" ? (en ? "Phone number" : "手机号码") : (en ? "Email address" : "邮箱地址");
  if (prefix) prefix.textContent = channel === "phone" ? "+86" : "@";
  if (input) {
    input.value = "";
    input.type = channel === "phone" ? "tel" : "email";
    input.inputMode = channel === "phone" ? "tel" : "email";
    input.placeholder = channel === "phone" ? (en ? "Phone number" : "请输入手机号") : (en ? "name@example.com" : "请输入邮箱地址");
    input.autocomplete = channel === "phone" ? "tel-national" : "email";
    input.focus();
  }
  screen.querySelector<HTMLElement>("[data-code-step]")?.setAttribute("hidden", "");
  setAuthError(screen, "");
}

async function submitCodeRequest(screen: HTMLElement): Promise<void> {
  const channel = screen.dataset.channel as PasswordlessChannel | undefined;
  const input = screen.querySelector<HTMLInputElement>("[data-destination]");
  const button = screen.querySelector<HTMLButtonElement>("[data-send-code]");
  if (!channel || !input || !button) return;
  const destination = input.value.trim();
  if (!destination) {
    setAuthError(screen, isEnglishLocale() ? "Enter your phone number or email." : "请输入手机号或邮箱地址");
    return;
  }
  button.disabled = true;
  try {
    const response = await requestCode(channel, destination);
    screen.querySelector<HTMLElement>("[data-code-step]")?.removeAttribute("hidden");
    const codeInput = screen.querySelector<HTMLInputElement>("[data-code]");
    if (response.developmentCode && codeInput) codeInput.value = response.developmentCode;
    codeInput?.focus();
    setAuthError(screen, "");
  } catch (error) {
    setAuthError(screen, messageFrom(error, isEnglishLocale() ? "Unable to send the code." : "暂时无法发送验证码"));
  } finally {
    button.disabled = false;
  }
}

async function submitCodeVerification(screen: HTMLElement): Promise<void> {
  const channel = screen.dataset.channel as PasswordlessChannel | undefined;
  const destination = screen.querySelector<HTMLInputElement>("[data-destination]")?.value.trim() ?? "";
  const code = screen.querySelector<HTMLInputElement>("[data-code]")?.value.trim() ?? "";
  if (!channel || !/^\d{6}$/.test(code)) {
    setAuthError(screen, isEnglishLocale() ? "Enter the 6-digit code." : "请输入 6 位验证码");
    return;
  }
  try {
    await verifyCode(channel, destination, code);
  } catch (error) {
    setAuthError(screen, messageFrom(error, isEnglishLocale() ? "Verification failed." : "验证失败，请重试"));
  }
}

function setAuthError(screen: HTMLElement, message: string): void {
  const target = screen.querySelector<HTMLElement>("[data-login-error]");
  if (target) target.textContent = message;
}

async function openMembership(): Promise<void> {
  if (currentSheet) return;
  const overlay = document.createElement("div");
  overlay.className = "zhiji-native-overlay";
  overlay.innerHTML = `
    <section class="zhiji-native-sheet" role="dialog" aria-modal="true" aria-label="${localize("会员与支付")}">
      <div class="zhiji-native-sheet-handle"></div>
      <header><div><h2>${localize("会员与支付")}</h2><p>${localize("数字会员由系统商店保障，服务费用由 Antom 安全收款")}</p></div><button type="button" data-close aria-label="${localize("关闭")}">×</button></header>
      <div class="zhiji-native-products"><p class="zhiji-native-loading">${localize("正在读取可购买项目…")}</p></div>
      <footer><button type="button" data-restore>${localize("恢复购买")}</button><button type="button" data-manage>${localize("管理订阅")}</button></footer>
    </section>`;
  document.body.append(overlay);
  currentSheet = overlay;
  overlay.querySelector("[data-close]")?.addEventListener("click", closeMembership);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) closeMembership();
  });
  overlay
    .querySelector("[data-restore]")
    ?.addEventListener("click", () => void restorePurchases());
  overlay
    .querySelector("[data-manage]")
    ?.addEventListener("click", () =>
      NativePurchases.manageSubscriptions().catch((error) =>
        notify(messageFrom(error, "暂时无法打开订阅管理")),
      ),
    );

  const host = overlay.querySelector<HTMLElement>(".zhiji-native-products");
  if (!host) return;
  try {
    const config = await getRemoteConfig();
    await loadStorePrices(config.products);
    renderProducts(host, config.products);
  } catch {
    host.replaceChildren(
      textBlock("支付服务尚未完成部署或配置，当前不会产生任何扣款。"),
    );
  }
}

function closeMembership(): void {
  currentSheet?.remove();
  currentSheet = undefined;
}

async function loadStorePrices(products: CatalogProduct[]): Promise<void> {
  const identifiers = products
    .filter((product) => product.provider !== "antom" && product.platformProductId)
    .map((product) => product.platformProductId as string);
  if (!identifiers.length) return;
  try {
    const response = await NativePurchases.getProducts({
      productIdentifiers: identifiers,
      productType: PURCHASE_TYPE.SUBS,
    });
    for (const product of response.products) {
      storeProducts.set(product.planIdentifier ?? product.identifier, product);
      storeProducts.set(product.identifier, product);
    }
  } catch {
    // 尚未在商店创建 SKU 时使用服务端展示文案，不伪造价格。
  }
}

function renderProducts(host: HTMLElement, products: CatalogProduct[]): void {
  host.replaceChildren();
  if (!products.length) {
    host.append(textBlock("暂无可购买项目，请在运营后台配置商品后重试。"));
    return;
  }

  for (const product of products) {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "zhiji-native-product";

    const copy = document.createElement("span");
    const title = document.createElement("strong");
    title.textContent = product.name;
    const detail = document.createElement("small");
    detail.textContent = product.description;
    copy.append(title, detail);

    const price = document.createElement("b");
    const storeProduct = product.platformProductId
      ? storeProducts.get(product.platformProductId)
      : undefined;
    price.textContent = storeProduct?.priceString ?? product.displayPrice ?? "待配置";
    card.append(copy, price);
    card.addEventListener("click", () => void purchase(product, card));
    host.append(card);
  }
}

async function purchase(product: CatalogProduct, button: HTMLButtonElement): Promise<void> {
  if (!localStorage.getItem(WEB_TOKEN_KEY)) {
    closeMembership();
    notify("请先登录后再购买");
    window.location.assign("/login?redirect=/profile#member");
    return;
  }
  button.disabled = true;
  try {
    if (product.provider === "antom") {
      await purchaseWithAntom(product);
    } else {
      await purchaseFromStore(product);
    }
  } catch (error) {
    if (!isUserCancellation(error)) {
      notify(messageFrom(error, "支付未完成，请稍后重试"));
    }
  } finally {
    button.disabled = false;
  }
}

async function purchaseFromStore(product: CatalogProduct): Promise<void> {
  if (!product.platformProductId) throw new Error("商店商品尚未配置");
  const order = await apiJson<{ orderId: string; appAccountToken: string }>(
    "/payments/store/order",
    { method: "POST", body: JSON.stringify({ productId: product.id }) },
  );
  let storeProduct = storeProducts.get(product.platformProductId);
  if (!storeProduct) {
    const response = await NativePurchases.getProducts({
      productIdentifiers: [product.platformProductId],
      productType: PURCHASE_TYPE.SUBS,
    });
    storeProduct = response.products[0];
  }
  if (!storeProduct) throw new Error("无法从系统商店读取该商品");

  const transaction = await NativePurchases.purchaseProduct({
    productIdentifier: storeProduct.planIdentifier ?? product.platformProductId,
    planIdentifier: platform === "android" ? storeProduct.identifier : undefined,
    offerToken: storeProduct.offerToken,
    productType: PURCHASE_TYPE.SUBS,
    appAccountToken: order.appAccountToken,
    autoAcknowledgePurchases: false,
  });
  const result = await apiJson<{ verified: boolean; entitlementTier?: string }>(
    "/payments/store/verify",
    {
      method: "POST",
      body: JSON.stringify({ orderId: order.orderId, transaction }),
    },
  );
  if (!result.verified) throw new Error("商店凭证尚未通过验证");
  await NativePurchases.acknowledgePurchase({
    purchaseToken: transaction.purchaseToken ?? transaction.transactionId,
  });
  await refreshEntitlements();
  closeMembership();
  notify("会员权益已开通");
}

async function purchaseWithAntom(product: CatalogProduct): Promise<void> {
  const checkout = await apiJson<{ orderId: string; continuationUrl: string }>(
    "/payments/antom/create",
    { method: "POST", body: JSON.stringify({ productId: product.id }) },
  );
  await SecureStorage.set("pending_payment_order", checkout.orderId);
  await Browser.open({ url: checkout.continuationUrl, presentationStyle: "popover" });
  void pollOrder(checkout.orderId);
}

async function pollOrder(orderId: string): Promise<void> {
  for (let attempt = 0; attempt < 24; attempt += 1) {
    await delay(attempt < 2 ? 1500 : 5000);
    try {
      const order = await apiJson<{ status: string }>(`/payments/orders/${orderId}`);
      if (order.status === "paid") {
        await Browser.close().catch(() => undefined);
        await SecureStorage.remove("pending_payment_order");
        closeMembership();
        notify("支付成功");
        return;
      }
      if (["failed", "cancelled", "refunded"].includes(order.status)) {
        notify("支付未完成");
        return;
      }
    } catch {
      // 弱网时继续查询；最终状态以服务端验签结果为准。
    }
  }
  notify("订单仍在处理中，可稍后在订单中查看结果");
}

async function restorePurchases(): Promise<void> {
  try {
    await NativePurchases.restorePurchases();
    const { purchases } = await NativePurchases.getPurchases({
      productType: PURCHASE_TYPE.SUBS,
      onlyCurrentEntitlements: platform === "ios",
    });
    await apiJson("/payments/store/restore", {
      method: "POST",
      body: JSON.stringify({ transactions: purchases }),
    });
    await refreshEntitlements();
    notify("购买记录已恢复");
  } catch (error) {
    notify(messageFrom(error, "恢复购买失败"));
  }
}

async function refreshEntitlements(): Promise<void> {
  const token = localStorage.getItem(WEB_TOKEN_KEY);
  if (!token) return;
  try {
    const response = await apiJson<{
      membership?: { tier: "basic" | "plus" | "pro"; expiresAt?: string } | null;
    }>("/entitlements");
    // A request started before logout or account switching must not restore the old account's rights.
    if (localStorage.getItem(WEB_TOKEN_KEY) !== token) return;
    if (response.membership) {
      localStorage.setItem(
        MEMBER_KEY,
        JSON.stringify({
          tier: response.membership.tier,
          since: new Date().toISOString(),
          expiresAt: response.membership.expiresAt,
          verified: true,
        }),
      );
      window.dispatchEvent(new StorageEvent("storage", { key: MEMBER_KEY }));
    } else if (response.membership == null) {
      clearCachedMembership();
    }
  } catch {
    // 离线时保留最近一次经服务端确认的本地显示状态。
  }
}

function clearCachedMembership(): void {
  localStorage.removeItem(MEMBER_KEY);
  window.dispatchEvent(new StorageEvent("storage", { key: MEMBER_KEY }));
}

function installDeepLinkHandler(): void {
  void App.addListener("appUrlOpen", ({ url }) => {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return;
    }
    if (parsed.hostname === "open") {
      const path = parsed.searchParams.get("path");
      if (path?.startsWith("/") && !path.startsWith("//")) window.location.assign(path);
      return;
    }
    if (parsed.hostname !== "payment-return" && parsed.pathname !== "/payment-return") {
      return;
    }
    void Browser.close().catch(() => undefined);
    const orderId = parsed.searchParams.get("orderId");
    if (orderId) void pollOrder(orderId);
  });
}

async function apiJson<T = unknown>(
  path: string,
  init: RequestInit & { authenticated?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("X-Zhiji-Platform", platform);
  if (init.body) headers.set("Content-Type", "application/json");
  if (init.authenticated !== false) {
    const token = localStorage.getItem(WEB_TOKEN_KEY);
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(`${apiBaseUrl}${API_PREFIX}${path}`, {
    ...init,
    headers,
  });
  const payload = (await response.json().catch(() => {
    throw new Error("服务连接异常，请稍后重试");
  })) as {
    error?: string;
    message?: string;
  };
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("服务返回异常，请稍后重试");
  }
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `请求失败（${response.status}）`);
  }
  return payload as T;
}

function providerLabel(provider: AuthProvider): string {
  return localize(provider === "apple"
    ? "通过 Apple 登录"
    : "通过 Google 登录");
}

function providerActionLabel(provider: AuthProvider): string {
  return localize(provider === "apple"
    ? "使用 Apple 继续"
    : "使用 Google 继续");
}

function providerIcon(provider: AuthProvider): string {
  if (provider === "apple") {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17.05 12.54c-.03-3.06 2.5-4.53 2.61-4.6a5.62 5.62 0 0 0-4.43-2.4c-1.86-.2-3.67 1.12-4.62 1.12-1 0-2.5-1.1-4.12-1.07A5.87 5.87 0 0 0 1.55 8.6c-2.14 3.7-.55 9.14 1.5 12.14 1.02 1.46 2.2 3.1 3.82 3.04 1.58-.06 2.17-.98 4.08-.98 1.9 0 2.45.98 4.1.94 1.7-.03 2.78-1.47 3.76-2.94a12.1 12.1 0 0 0 1.72-3.5 5.29 5.29 0 0 1-3.48-4.76ZM14.02 3.57A5.36 5.36 0 0 0 15.25 0a5.46 5.46 0 0 0-3.52 1.7 5.11 5.11 0 0 0-1.26 3.43 4.52 4.52 0 0 0 3.55-1.56Z"/></svg>';
  }
  if (provider === "google") {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.29-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.94-6.16-4.54H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.36c1.62 0 3.06.56 4.2 1.64l3.17-3.16A10.62 10.62 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.36 12 5.36Z"/></svg>';
  }
  return "";
}

function randomState(): string {
  return crypto.randomUUID().replaceAll("-", "");
}

function postLoginPath(): string {
  const requested = new URLSearchParams(window.location.search).get("redirect");
  return requested?.startsWith("/") && !requested.startsWith("//") ? requested : "/profile";
}

function isEnglishLocale(): boolean {
  return document.documentElement.lang.toLowerCase().startsWith("en");
}

function localize(value: string): string {
  return window.__ZHIJI_I18N__?.translate(value) ?? value;
}

function matchesLocalized(value: string | undefined, source: string): boolean {
  if (!value) return false;
  const normalized = value.replaceAll(/\s/g, "");
  return (
    normalized === source.replaceAll(/\s/g, "") ||
    normalized === localize(source).replaceAll(/\s/g, "")
  );
}

function isUserCancellation(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "USER_CANCELLED"
  );
}

function messageFrom(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function textBlock(message: string): HTMLElement {
  const paragraph = document.createElement("p");
  paragraph.className = "zhiji-native-empty";
  paragraph.textContent = localize(message);
  return paragraph;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function notify(message: string): void {
  document.querySelector(".zhiji-native-toast")?.remove();
  const toast = document.createElement("div");
  toast.className = "zhiji-native-toast";
  toast.textContent = localize(message);
  document.body.append(toast);
  window.setTimeout(() => toast.remove(), 3200);
}

function installStyles(): void {
  if (document.getElementById("zhiji-native-services-style")) return;
  const style = document.createElement("style");
  style.id = "zhiji-native-services-style";
  style.textContent = `
    body.zhiji-native-login-active{overflow:hidden!important}
    body.zhiji-native-login-active [code-path="src/components/Toast.tsx:29:5"]{z-index:2147483647!important}
    .zhiji-native-login-screen{position:fixed;z-index:2147483600;inset:0;overflow:auto;background:radial-gradient(circle at 50% 4%,rgba(170,126,58,.13),transparent 30%),#0b100e;color:#f5efe2;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","PingFang SC",sans-serif;-webkit-font-smoothing:antialiased}
    .zhiji-native-login-shell{box-sizing:border-box;display:flex;min-height:100%;max-width:1120px;margin:0 auto;padding:env(safe-area-inset-top) 24px env(safe-area-inset-bottom);flex-direction:column}
    .zhiji-native-login-toolbar{position:relative;display:flex;min-height:56px;align-items:center;justify-content:center;color:#a69f92;font-size:14px;font-weight:600;letter-spacing:.16em}
    .zhiji-native-login-toolbar button{position:absolute;left:-10px;display:grid;width:44px;height:44px;padding:0;place-items:center;border:0;background:transparent;color:#f5efe2}
    .zhiji-native-login-toolbar button svg{width:25px;height:25px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
    .zhiji-auth-layout{display:grid;flex:1;align-items:center}.zhiji-native-login-screen main{display:flex;padding:22px 0 16px;flex-direction:column}.zhiji-auth-intro{display:none}
    .zhiji-native-login-mark{position:relative;display:grid;width:62px;height:62px;margin:0 auto 20px;place-items:center;border:1px solid rgba(212,162,78,.46);border-radius:20px;background:linear-gradient(145deg,rgba(212,162,78,.18),rgba(212,162,78,.04));box-shadow:0 18px 45px rgba(0,0,0,.28),inset 0 1px rgba(255,255,255,.08);transform:rotate(45deg)}
    .zhiji-native-login-mark:after{position:absolute;inset:7px;border:1px solid rgba(212,162,78,.22);border-radius:15px;content:""}
    .zhiji-native-login-mark span{position:relative;z-index:1;color:#e0b66d;font-family:Georgia,"Songti SC",serif;font-size:27px;font-weight:700;transform:rotate(-45deg)}
    .zhiji-native-login-screen h1,.zhiji-native-login-screen h2{margin:0;color:#f5efe2;font-family:Georgia,"Songti SC",serif;font-size:28px;line-height:1.25;font-weight:800;letter-spacing:.04em}.zhiji-native-login-screen h2{text-align:center}
    .zhiji-native-login-lead{margin:9px 0 28px;text-align:center;color:#8e897d;font-size:13px;line-height:1.5}
    .zhiji-native-auth-mode{display:grid;grid-template-columns:1fr 1fr;margin-bottom:22px;padding:4px;border:1px solid #343c37;border-radius:14px;background:#101512}
    .zhiji-native-auth-mode button{min-height:42px;border:0;border-radius:10px;background:transparent;color:#777c77;font-size:14px;font-weight:700;transition:background .15s,color .15s}.zhiji-native-auth-mode button.is-active{background:rgba(212,162,78,.14);color:#e0b66d}
    .zhiji-native-auth-method-title{margin:0 0 10px;color:#8e897d;font-size:12px}
    .zhiji-native-passwordless-form[hidden],.zhiji-native-provider-list[hidden]{display:none}.zhiji-native-passwordless-form{margin-top:2px}.zhiji-native-passwordless-form>label,.zhiji-native-code-step>label{display:block;margin:14px 0 8px;color:#aaa397;font-size:12px;font-weight:500}.zhiji-auth-change{padding:0;border:0;background:transparent;color:#d4a24e;font-size:12px}
    .zhiji-native-phone-field{display:flex;height:52px;align-items:center;border:1px solid #343c37;border-radius:14px;background:#151b18;transition:border-color .2s,box-shadow .2s}
    .zhiji-native-phone-field:focus-within{border-color:#a77c3c;box-shadow:0 0 0 3px rgba(212,162,78,.1)}
    .zhiji-native-phone-field>span{padding:0 14px;border-right:1px solid #303733;color:#d6d0c4;font-size:14px}
    .zhiji-native-phone-field input,.zhiji-native-code-step input{box-sizing:border-box;width:100%;height:52px;padding:0 14px;border:1px solid #343c37;border-radius:14px;outline:0;background:#151b18;color:#f5efe2;font-size:16px;caret-color:#d4a24e}
    .zhiji-native-phone-field input{height:48px;border:0;background:transparent}
    .zhiji-native-phone-field input::placeholder,.zhiji-native-code-step input::placeholder{color:#686d68}.zhiji-native-email-icon{position:absolute;left:18px;color:#d4a24e;font-size:20px;font-weight:700}
    .zhiji-native-code-step input:focus{border-color:#a77c3c;box-shadow:0 0 0 3px rgba(212,162,78,.1)}
    .zhiji-native-login-primary{width:100%;min-height:50px;margin-top:12px;border:0;border-radius:14px;background:linear-gradient(135deg,#deb86f,#a77a38);color:#0b100e;font-size:15px;font-weight:750;box-shadow:0 9px 24px rgba(139,96,35,.18);transition:transform .15s,opacity .15s}
    .zhiji-native-login-primary:active{transform:scale(.985)}.zhiji-native-login-primary:disabled{opacity:.45;box-shadow:none}
    .zhiji-native-login-error{min-height:16px;margin:5px 2px -5px;color:#d88179;font-size:11px;line-height:16px}
    .zhiji-native-code-step{margin-top:20px}.zhiji-native-code-step[hidden]{display:none}
    .zhiji-native-login-hint{margin:7px 2px 0;color:#837d72;font-size:11px;line-height:1.5}
    .zhiji-native-login-divider{display:flex;margin:23px 0 15px;align-items:center;gap:12px;color:#6e716c;font-size:11px;white-space:nowrap}
    .zhiji-native-login-divider:before,.zhiji-native-login-divider:after{height:1px;flex:1;background:#2b322e;content:""}
    .zhiji-native-provider-list{display:grid;gap:10px}
    .zhiji-native-provider{position:relative;display:flex;width:100%;min-height:50px;align-items:center;justify-content:center;border:1px solid #343c37;border-radius:14px;background:#141a17;color:#eee8dc;font-size:14px;font-weight:600;letter-spacing:.01em;transition:background .15s,transform .15s}
    .zhiji-native-provider:active{background:#1b221e;transform:scale(.985)}
    .zhiji-native-provider.is-selected{border-color:#a77c3c;background:rgba(212,162,78,.1);color:#e0b66d}
    .zhiji-native-provider svg{position:absolute;left:18px;width:21px;height:21px}
    .zhiji-native-phone-icon{position:absolute;left:16px;min-width:28px;text-align:center;color:#d4a24e;font-size:13px;font-weight:750}
    .zhiji-native-provider-apple{background:#f3f0e9;color:#111511;border-color:#f3f0e9}.zhiji-native-provider-apple:active{background:#dcd9d2}
    .zhiji-auth-legal{margin:20px 8px 0;text-align:center;color:#676b66;font-size:10px;line-height:1.65}
    @media(min-width:800px){.zhiji-native-login-screen{background:radial-gradient(circle at 20% 25%,rgba(170,126,58,.17),transparent 32%),#0b100e}.zhiji-native-login-toolbar{justify-content:flex-start;padding-left:58px}.zhiji-auth-layout{grid-template-columns:minmax(0,1fr) 440px;gap:80px;padding:36px 44px 80px}.zhiji-auth-intro{display:block;max-width:520px}.zhiji-auth-intro .zhiji-native-login-mark{margin:0 0 38px 12px}.zhiji-auth-intro>p{margin:0 0 14px;color:#d4a24e;font-size:13px;letter-spacing:.18em}.zhiji-auth-intro h1{max-width:500px;font-size:46px;line-height:1.2;letter-spacing:.02em}.zhiji-auth-intro ul{display:grid;gap:12px;margin:32px 0 0;padding:0;list-style:none;color:#9e988d;font-size:14px}.zhiji-auth-intro li:before{margin-right:10px;color:#d4a24e;content:"✦"}.zhiji-auth-card{box-sizing:border-box;padding:34px!important;border:1px solid #303832;border-radius:24px;background:rgba(18,24,21,.92);box-shadow:0 28px 80px rgba(0,0,0,.35)}.zhiji-native-login-screen h2{text-align:left}.zhiji-native-login-lead{text-align:left!important;margin-bottom:24px!important}.zhiji-native-provider-list{gap:12px}}
    @media(max-height:760px){.zhiji-native-login-screen main{padding-top:8px}.zhiji-native-login-mark{width:52px;height:52px;margin-bottom:14px}.zhiji-native-login-screen h1{font-size:24px}.zhiji-native-login-lead{margin-bottom:20px}.zhiji-native-login-divider{margin:18px 0 12px}.zhiji-native-provider-list{gap:8px}.zhiji-native-provider{min-height:46px}}
    .zhiji-native-overlay{position:fixed;z-index:2147483640;inset:0;display:flex;align-items:flex-end;justify-content:center;background:rgba(0,0,0,.64);backdrop-filter:blur(8px)}
    .zhiji-native-sheet{box-sizing:border-box;width:100%;max-width:520px;max-height:88dvh;overflow:auto;padding:10px 18px calc(18px + env(safe-area-inset-bottom));border:1px solid #343a35;border-radius:24px 24px 0 0;background:#101512;color:#f2ebdc;box-shadow:0 -20px 60px rgba(0,0,0,.45)}
    .zhiji-native-sheet-handle{width:38px;height:4px;margin:0 auto 14px;border-radius:9px;background:#4c514c}
    .zhiji-native-sheet header{display:flex;gap:12px;align-items:flex-start;justify-content:space-between}.zhiji-native-sheet h2{margin:0;font:700 22px/1.2 Georgia,serif}.zhiji-native-sheet header p{margin:6px 0 0;color:#8e897d;font:11px/1.55 system-ui}.zhiji-native-sheet header button{border:0;background:transparent;color:#a8a196;font:28px/1 system-ui}
    .zhiji-native-products{display:grid;gap:10px;margin:18px 0}.zhiji-native-product{display:flex;align-items:center;justify-content:space-between;gap:16px;width:100%;padding:15px;border:1px solid #343a35;border-radius:15px;background:#181d1a;color:#f2ebdc;text-align:left}.zhiji-native-product span{min-width:0}.zhiji-native-product strong,.zhiji-native-product small{display:block}.zhiji-native-product strong{font:650 15px system-ui}.zhiji-native-product small{margin-top:5px;color:#8e897d;font:11px/1.45 system-ui}.zhiji-native-product b{flex:none;color:#d4a24e;font:700 14px system-ui}.zhiji-native-product:disabled{opacity:.55}
    .zhiji-native-sheet footer{display:flex;gap:8px}.zhiji-native-sheet footer button{flex:1;min-height:42px;border:1px solid #343a35;border-radius:12px;background:transparent;color:#b5aea0;font:12px system-ui}.zhiji-native-empty,.zhiji-native-loading{padding:28px 12px;color:#8e897d;font:13px/1.7 system-ui;text-align:center}
    .zhiji-native-toast{position:fixed;z-index:2147483647;left:50%;bottom:calc(88px + env(safe-area-inset-bottom));max-width:calc(100vw - 42px);transform:translateX(-50%);padding:11px 16px;border:1px solid rgba(212,162,78,.35);border-radius:12px;background:rgba(16,21,18,.96);color:#f2ebdc;font:13px/1.45 system-ui;box-shadow:0 10px 32px rgba(0,0,0,.4)}
  `;
  document.head.append(style);
}
